import {
  DocumentServicesObject,
  EditorClientSpecMapEntry,
} from '@wix/document-services-types';
import { subscriber } from '@wix/realtime';
import { createClient } from '@wix/sdk';
import type { AuthenticationStrategy } from '@wix/sdk-types';
import { PublicInterfaces } from '@wix/editor-platform-interfaces';
import type { IPlatformInfraPublicAPI } from '@wix/editor-platform-worker';
import { AppRefreshService } from '../SdkEventHandlerService/services/appRefreshService/appRefreshService';
import { getHostEventData } from '../../utils/getHostEventData';

interface PlatformRealtimeRouterRefreshAppSubscription {
  destroy: () => void;
}

const hasEditorReactComponents = (app: EditorClientSpecMapEntry): boolean =>
  (app?.components ?? []).some((c) => c.type === 'EDITOR_REACT_COMPONENT');

export interface PlatformRealtimeRouterRefreshService {
  destroy: () => void;
}

export const createPlatformRealtimeRouterRefreshAppSubscription = (
  appDefinitionId: string,
  auth: AuthenticationStrategy,
  appRefreshService: Pick<AppRefreshService, 'refreshRouters'>,
): PlatformRealtimeRouterRefreshAppSubscription => {
  const client = createClient({
    modules: { subscriber },
    auth,
  });

  const subscriptionId = client.subscriber.subscribe(
    PublicInterfaces.PlatformRealtimeChannels.ROUTER_REFRESH,
    () => {
      appRefreshService.refreshRouters({ appDefinitionId });
    },
    {
      onSubscriptionError: (error, currentSubscriptionId) => {
        console.error(
          'Platform realtime router refresh subscription error:',
          error,
          currentSubscriptionId,
        );
      },
    },
  );

  return {
    destroy: () => {
      client.subscriber.unsubscribe({
        subscriptionId,
      });
    },
  };
};

export const createPlatformRealtimeRouterRefreshService = (
  documentServicesAPI: DocumentServicesObject,
  platformInfraPublicAPI: IPlatformInfraPublicAPI,
  appRefreshService: Pick<AppRefreshService, 'refreshRouters'>,
) => {
  const subscriptions = new Map<
    string,
    PlatformRealtimeRouterRefreshAppSubscription
  >();

  const unsubscribeFromApp = (appDefinitionId: string) => {
    const subscription = subscriptions.get(appDefinitionId);

    if (!subscription) {
      return;
    }

    subscription.destroy();
    subscriptions.delete(appDefinitionId);
  };

  const subscribeToApp = (appDefinitionId: string) => {
    if (!appDefinitionId || subscriptions.has(appDefinitionId)) {
      return;
    }

    try {
      const subscription = createPlatformRealtimeRouterRefreshAppSubscription(
        appDefinitionId,
        platformInfraPublicAPI.getSDKAuth({
          appDefinitionId,
        }),
        appRefreshService,
      );
      subscriptions.set(appDefinitionId, subscription);
    } catch (error) {
      console.error(
        `Failed to initialize platform realtime router refresh subscription for ${appDefinitionId}:`,
        error,
      );
    }
  };

  const subscribeInstalledApps = () => {
    const installedApps = documentServicesAPI.platform.getInstalledApps();
    const uniqueAppDefinitionIds = new Set(
      installedApps
        .filter(hasEditorReactComponents)
        .map((app) => app.appDefinitionId)
        .filter((appDefinitionId): appDefinitionId is string =>
          Boolean(appDefinitionId),
        ),
    );

    uniqueAppDefinitionIds.forEach(subscribeToApp);
  };

  const unsubscribeFromAppEvents =
    documentServicesAPI.platform.subscribeOnAppEvents((event) => {
      const hostEvent = getHostEventData<{ appDefinitionId?: string }>(event);

      if (!hostEvent.appDefinitionId) {
        return;
      }

      if (hostEvent.eventType === PublicInterfaces.EventType.appInstalled) {
        const installedApp = documentServicesAPI.platform
          .getInstalledApps()
          .find((app) => app.appDefinitionId === hostEvent.appDefinitionId);

        if (installedApp && hasEditorReactComponents(installedApp)) {
          subscribeToApp(hostEvent.appDefinitionId);
        }
      }

      if (
        hostEvent.eventType === PublicInterfaces.EventType.removeAppCompleted
      ) {
        unsubscribeFromApp(hostEvent.appDefinitionId);
      }
    });

  const destroy = () => {
    unsubscribeFromAppEvents?.();
    subscriptions.forEach((subscription) => {
      subscription.destroy();
    });
    subscriptions.clear();
  };

  return {
    subscribeInstalledApps,
    destroy,
  };
};

export const initPlatformRealtimeRouterRefreshService = (
  documentServicesAPI: DocumentServicesObject,
  platformInfraPublicAPI: IPlatformInfraPublicAPI,
  appRefreshService: Pick<AppRefreshService, 'refreshRouters'>,
): PlatformRealtimeRouterRefreshService | null => {
  try {
    const service = createPlatformRealtimeRouterRefreshService(
      documentServicesAPI,
      platformInfraPublicAPI,
      appRefreshService,
    );

    service.subscribeInstalledApps();

    return service;
  } catch (error) {
    console.error(
      'Failed to initialize platform realtime router refresh service:',
      error,
    );
    return null;
  }
};
